import { TenantBrand, TenantColors } from "./types";

/**
 * CSS custom properties (from src/color-scheme.scss) that a brand overrides.
 * Kept to the small brand subset on purpose — neutrals, surfaces and the
 * light/dark theme stay owned by ThemeProvider (data-theme).
 *
 * NOTE: derived alpha tokens (--app-primary-01, --app-primary-015, landing
 * palette) are still hardcoded in color-scheme.scss and are NOT overridden
 * here yet. They can be added to this list once brand colors are finalized.
 */
const BRAND_TOKENS = {
  primary: "--app-primary",
  secondary: "--app-secondary",
  accent: "--app-accent",
} as const;

const TENANT_ATTR = "data-tenant";


/**
 * Apply a tenant brand by writing CSS variable overrides onto `target`.
 * `colors` is resolved separately by the caller (backend branding fetch,
 * falling back to registry.ts's DEFAULT_COLORS) — it is not part of `brand`.
 *
 * - Global usage (post-auth): pass document.documentElement so the whole app
 *   reskins, sitting on top of whichever data-theme (light/dark) is active.
 * - Local usage (pre-auth): pass a public-layout wrapper element so branding
 *   is scoped to public pages and cannot leak into authorized chrome.
 */
export function applyBrand(
  brand: TenantBrand,
  colors: TenantColors,
  target: HTMLElement = document.documentElement,
): void {
  target.style.setProperty(BRAND_TOKENS.primary, colors.primary);
  target.style.setProperty(BRAND_TOKENS.secondary, colors.secondary);
  if (colors.accent) {
    target.style.setProperty(BRAND_TOKENS.accent, colors.accent);
  } else {
    target.style.removeProperty(BRAND_TOKENS.accent);
  }
  target.setAttribute(TENANT_ATTR, brand.key);
}

/**
 * Remove all brand overrides from `target`, reverting to the color-scheme.scss
 * defaults. Call on logout / tenant switch to prevent cross-tenant bleed.
 */
export function clearBrand(target: HTMLElement = document.documentElement): void {
  Object.values(BRAND_TOKENS).forEach((cssVar) => target.style.removeProperty(cssVar));
  target.removeAttribute(TENANT_ATTR);
}

/**
 * Point the browser-tab favicon at `logoUrl` (the backend branding fetch's
 * `logo`, falling back to the app's default icon — see TenantProvider).
 * Global — the tab is not scoped.
 */
export function setFavicon(logoUrl: string): void {
  const existing = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  const link = existing ?? document.createElement("link");
  link.rel = "icon";
  link.href = logoUrl;
  if (!existing) {
    document.head.appendChild(link);
  }
}
